/**
 * Class fragments shared by more than one recipe.
 *
 * When the same visual behaviour has to look identical across components — the
 * focus ring on a button and on an input, say — it lives here so there is one
 * place to change it.
 */

/**
 * Keyboard-focus ring. Applied to every interactive element in the library.
 *
 * The `data-[force=focus]` twin is what lets the catalogue show a focus state
 * without stealing focus. It is written out rather than generated because
 * Tailwind reads class names from source — a string built at runtime produces
 * no CSS.
 */
export const focusRing =
  'outline-none focus-visible:ring-focus focus-visible:ring-ring focus-visible:ring-offset-0 ' +
  'data-[force=focus]:ring-focus data-[force=focus]:ring-ring data-[force=focus]:ring-offset-0';

/**
 * Focus ring for controls that live inside a container of their own (checkbox,
 * switch, radio) where an offset reads better than a flush ring.
 */
export const focusRingOffset =
  'outline-none focus-visible:ring-focus focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background ' +
  'data-[force=focus]:ring-focus data-[force=focus]:ring-ring data-[force=focus]:ring-offset-2 data-[force=focus]:ring-offset-background';

/** How a disabled interactive element looks. */
export const disabledState =
  'disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed';

/**
 * How a form control looks when validation has failed. Driven by aria-invalid
 * so the styling and the accessibility state can never drift apart.
 */
export const invalidState =
  'aria-[invalid=true]:border-destructive aria-[invalid=true]:focus-visible:ring-destructive';

/** The default transition for hover/focus colour changes. */
export const transitionBase = 'transition-colors duration-fast';

/** The chrome every text-entry control shares: border, background, radius. */
export const controlChrome =
  'w-full rounded-md border border-input bg-background text-foreground shadow-e1 placeholder:text-muted-foreground';

/**
 * ADOPTION MARKERS
 *
 * Every recipe carries `uiMarker`, so anything the library renders is findable
 * in the DOM. Turn the outlines on from /library (or `?debug-ui`) and any
 * control on screen *without* one was hand-built rather than taken from here.
 *
 * The class does nothing on its own — the outline rules live in tokens.css
 * behind `.debug-ui` on <html>, so this costs one class name in production and
 * nothing else.
 *
 * `uiMarkerText` is separate because Text is used ~869 times: outlining every
 * string on screen drowns out the signal, so it toggles on its own.
 */
export const uiMarker = 'relay-ui';
export const uiMarkerText = 'relay-ui-text';

/**
 * Layout primitives carry this *as well as* `uiMarker`, so the overlay can
 * colour them apart from the components that draw something.
 *
 * The distinction is the one that matters while standardising a screen: a red
 * outline says "this control came from the library", a violet one says "this
 * box is a Stack/Inline/Grid rather than a hand-written flex div", and an
 * un-outlined box is markup with nothing behind it. Reading a screen for
 * layout debt means looking for the third kind, and before this they were
 * indistinguishable from every div the library renders internally.
 */
export const uiMarkerLayout = 'relay-ui-layout';

/**
 * The library's own structural divs — Dialog's overlay, Card's padding box,
 * PageHeader's title column. Not components in their own right, and not debt.
 *
 * They carry a marker purely so the "plain div" overlay can skip them. The
 * amber rule is `div:not([class*="relay-ui"])`, a substring match, so this
 * excludes them; the red rule is `.relay-ui`, an exact class match, so it does
 * not light them up. That is the point: with the div overlay on by itself,
 * every outline left on screen is markup the product wrote by hand. Thirty-odd
 * unmarked divs inside Card and Dialog would otherwise repeat on every screen
 * and drown the signal they exist to show.
 */
export const uiMarkerInternal = 'relay-ui-internal';

/**
 * Warns when a call site passes a width that the recipe will silently beat.
 *
 * Dialog and Sheet set their width with a responsive variant — `sm:max-w-lg`
 * and friends. A call site writing `className="max-w-2xl"` uses the *base*
 * variant, which Tailwind emits earlier in the stylesheet, so from `sm` up the
 * recipe wins and the override does nothing at all. Twenty-seven dialogs in
 * the product were sized this way and every one of them rendered at the
 * default width.
 *
 * That is the component's fault rather than twenty-seven mistakes: each author
 * passed a Tailwind class, which is a reasonable thing to expect to work. The
 * component cannot honour it without giving up the responsive default, so the
 * least it can do is say so.
 */
export function warnOnUnresponsiveWidth(
  component: string,
  className: string | undefined
) {
  if (!import.meta.env.DEV || !className) return;
  const bare = className
    .split(/\s+/)
    .filter((c) => /^(max-)?w-/.test(c) && !c.includes(':'));
  if (!bare.length) return;
  console.warn(
    `[library/${component}] ${bare.join(', ')} will be ignored from the sm ` +
      'breakpoint up — the width comes from a responsive variant that lands ' +
      'later in the stylesheet. Use the `size` prop, or prefix the class ' +
      '(sm:max-w-2xl) to target the same breakpoint.'
  );
}
